import React, { useEffect, useRef, useState } from "react";
import { useGetMissingImages } from "../apis/get-missing-images";
import { Grid, CircularProgress, Box, Grow, Modal, Typography } from "@mui/material";
import ProductSkeleton from "components/ui/Skeleton/ProductSkeleton";
import { ProductCard } from "components/ui";
import { transformData } from "utils/transformdata";
import { useInView } from "react-intersection-observer";
import { useSettingContext } from "context/SettingContext";
import FileContainerComp from "components/ui/UploadImageModal/FileContainerComp";
import TableContainerComp from "components/ui/UploadImageModal/TableContainerComp";
import { ReactComponent as Cross } from "../../../../assets/icons/update_img_icons/cross.svg";
import Lottie from "lottie-react";
import SuccessAnimation from "../../../../assets/icons/update_img_icons/success.json";
import apiClient from "lib/api-client";
import { useGlobalContext } from "context/GlobalContext";

const MissingImages = () => {
  const [countSet, setCountSet] = useState(false);
  const { setMissingImagesCount, search, setSearch, setSyncButton, setSyncButtonName, missingImagesModal, setMissingImagesModal } = useSettingContext();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error } = useGetMissingImages({
    limit: 30,
    search,
  });
  const { setFilterChangeCount, setDisplayCount } = useGlobalContext();

  // const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  // const [dragging, setDragging] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [designStatus, setDesignStatus] = useState([]);
  const [missingImageData, setMissingImageData] = useState([]);
  useEffect(() => {
    setFilterChangeCount(data?.pages[0]?.totalCount);
    // setFilterChangeDesignCount(data?.pages[0]?.totalStyles);
    setDisplayCount(true);
    return () => {
      setFilterChangeCount(0);
      // setFilterChangeDesignCount(0);
      setDisplayCount(false);
    };
  }, [data]);

  const uploadAndCheckDesign = async (files) => {
    try {
      const styleNumber = files.map((file) => (file.updatedName ? file.updatedName : file.name.split(".").slice(0, -1).join(".")));

      const response = await apiClient.post("/api/v1/stylesImage/checkDesignNumber", { styleNumber, type: "Missing_Images" });

      const data = response.data;

      const resultMap = data.reduce((acc, item) => {
        acc[item.styleNumber] = item.exists ? "Design Found" : "Design Not Found";
        return acc;
      }, {});

      const updatedSelectedFiles = files.map((file) => {
        const styleKey = file.updatedName || file.name.split(".").slice(0, -1).join(".");
        const existsStatus = resultMap[styleKey] === "Design Found"; // true or false

        file.exists = existsStatus;

        return file;
      });

      setSelectedFiles(updatedSelectedFiles);

      const updatedDesignStatus = files.map((file) => {
        const styleNumber = file.updatedName || file.name.split(".").slice(0, -1).join(".");
        const status = resultMap[styleNumber] || "Error Checking Design";

        return status;
      });

      setDesignStatus(updatedDesignStatus);
    } catch (error) {
      console.error("Error checking design:", error);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);

    const validImages = [];

    files.forEach((file) => {
      const isImage = file.type.startsWith("image/");

      if (!isImage) {
        alert("Only image files are allowed.");
      }

      if (isImage) {
        validImages.push(file);
      }
    });

    if (validImages.length > 0) {
      const updatedFiles = [...selectedFiles, ...validImages];
      setSelectedFiles(updatedFiles);
      await uploadAndCheckDesign(updatedFiles);
    }

    e.target.value = "";
  };

  // const handleContainerClick = () => {
  //   fileInputRef.current.click();
  // };

  // Function to handle file drop
  const handleDrop = async (e) => {
    e.preventDefault();
    // setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const validImages = [];

    files.forEach((file) => {
      const isImage = file.type.startsWith("image/");

      if (!isImage) {
        alert("Only image files are allowed.");
      }

      if (isImage) {
        validImages.push(file);
      }
    });

    if (validImages.length > 0) {
      const updatedFiles = [...selectedFiles, ...validImages];
      setSelectedFiles(updatedFiles);
      await uploadAndCheckDesign(updatedFiles);
    }
  };

  // Function to handle drag over
  // const handleDragOver = (e) => {
  //   e.preventDefault();
  //   setDragging(true);
  // };

  // Function to handle drag leave
  // const handleDragLeave = () => {
  //   setDragging(false);
  // };

  const handleDeleteImg = (indexToDelete) => {
    const updatedFiles = selectedFiles.filter((_, index) => index !== indexToDelete);
    const updatedDesignStatus = designStatus.filter((_, index) => index !== indexToDelete);

    setSelectedFiles(updatedFiles);
    setDesignStatus(updatedDesignStatus);
  };

  // const handleClear = () => {
  //   setSelectedFiles([]);
  // };

  const handleModalClose = () => {
    setMissingImagesModal(false);
    setSelectedFiles([]);
    setUploadSuccess(false);
  };

  const handleMissingSubmitBtn = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select at least one image before submitting.");
      return;
    }

    const formData = new FormData();

    selectedFiles.forEach((file) => {
      if (file.exists) {
        const newName = file.updatedName || file.name;
        const renamedFile = new File([file], newName.replace(/\.[^.]+$/, ".jpg"), {
          type: "image/jpeg",
        });

        formData.append("images", renamedFile);
      }
    });

    try {
      const response = await apiClient.post("/api/v1/stylesImage/upload_Images?type=Missing_Images", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        setUploadSuccess(true);
        setSelectedFiles([]);
      } else {
        alert("Failed to upload. Please try again.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("An error occurred during upload.");
    }
  };

  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  useEffect(() => {
    let combineData = data?.pages?.flatMap((page) => page?.data);
    setMissingImageData(combineData);
    if (data?.pages?.[0]?.totalCount !== undefined && !countSet) {
      setMissingImagesCount(data.pages[0].totalCount);
      setCountSet(true);
    }
  }, [data?.pages, setMissingImagesCount, countSet]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  useEffect(() => {
    setSyncButtonName("missing-images");
    setSyncButton(true);
    return () => {
      setSyncButton(false);
      setSyncButtonName(null);
      setSearch("");
    };
  }, [setSearch]);

  if (isLoading) return <ProductSkeleton count={8} />;

  if (isError) return <div>Error: {error.message}</div>;

  return (
    <>
      <Grid
        container
        className="Grid-padding"
        direction="row"
        justifyContent="flex-start"
        sx={{
          flex: "1 1 1",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "30px",
          rowGap: "32px",
          // maxHeight: "78vh",
          // overflowY: "scroll",
          // "&::-webkit-scrollbar": {
          //   width: "0px",
          // },
          // "&::-webkit-scrollbar-thumb": {
          //   background: "transparent",
          // },
        }}
      >
        {data?.pages?.map((page) =>
          page?.data?.map((design) => {
            const data = transformData({ ...design, restrict: false });
            return (
              <Grid item className="product">
                <ProductCard data={data} key={design._id} style={{ maxWidth: "430px", minWidth: "430px" }} allData={missingImageData} />
              </Grid>
            );
          })
        )}
        <div ref={ref} style={{ height: "20px", marginTop: "10px" }} />
        {isFetchingNextPage && (
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <CircularProgress />
          </Grid>
        )}
      </Grid>

      {/* ImageUploadModal */}
      <Modal open={missingImagesModal} onClose={handleModalClose}>
        <Grow in={missingImagesModal}>
          <Box
            sx={{
              backgroundColor: "#101010",
              padding: "18px",
              margin: "auto",
              width: "fit-content",
              marginTop: "18vh",
              borderRadius: "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              // border: "2px solid yellow",
            }}
          >
            <Box>
              {/* Success Message UI */}
              {uploadSuccess ? (
                <>
                  <Box display="flex" justifyContent="flex-end">
                    <Box sx={{ cursor: "pointer" }} onClick={handleModalClose}>
                      <Cross />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      height: "492px",
                      width: "907px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box height="185px" width="185px">
                      <Lottie animationData={SuccessAnimation} />
                    </Box>

                    <Typography fontFamily="Poppins" fontWeight="500" fontSize="32px" lineHeight="22px" letterSpacing="-0.41px" color="white" textAlign="center">
                      {/* {`${selectedFiles.length} ${selectedFiles.length > 1 ? "Images have" : "Image has"} been uploaded successfully`} */}
                      {`${designStatus.filter((status) => status === "Design Found").length} ${designStatus.filter((status) => status === "Design Found").length > 1 ? "Designs have" : "Design has"} been uploaded successfully`}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  {/* File Upload UI */}
                  {selectedFiles.length === 0 ? (
                    <>
                      <FileContainerComp
                        MissingHeaderText="Upload Missing Images"
                        // onclick={handleContainerClick}
                        handleDrop={handleDrop}
                        handleFileChange={handleFileChange}
                        // handleDragOver={handleDragOver}
                        // handleDragLeave={handleDragLeave}
                        onClose={handleModalClose}
                        // dragging={dragging}
                        fileheight={"492px"}
                      />
                      {/* <input type="file" ref={fileInputRef} multiple accept="image/*" style={{ display: "none" }} onChange={handleFileChange} /> */}
                    </>
                  ) : (
                    <TableContainerComp
                      selectedFiles={selectedFiles}
                      setSelectedFiles={setSelectedFiles}
                      handleSubmitBtn={handleMissingSubmitBtn}
                      handleDeleteImg={handleDeleteImg}
                      // onclear={handleClear}
                      AddImagesBtn={false}
                      uploadAndCheckDesign={uploadAndCheckDesign}
                      designStatus={designStatus}
                      onClose={handleModalClose}
                      // setDesignStatus={setDesignStatus}
                    />
                  )}
                </>
              )}
            </Box>
          </Box>
        </Grow>
      </Modal>
    </>
  );
};

export default MissingImages;
